import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { openRazorpayCheckout, loadRazorpayScript } from "../lib/razorpay";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area
} from "recharts";
import { Flame, Trophy, Award, FileSearch, MessagesSquare, Target, Sparkles, ArrowRight, Gift, Copy, Check, Calendar, AlertTriangle, Download, Receipt, Shield, Clock, Loader2, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

function readinessColor(s){ if(s<=40) return "#EF4444"; if(s<=70) return "#EAB308"; return "#22C55E"; }
function readinessLabel(s){
  if(s<=20) return "Beginner";
  if(s<=40) return "Learning";
  if(s<=60) return "Improving";
  if(s<=80) return "Competitive";
  if(s<=90) return "Recruiter Ready";
  return "Offer Ready";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [manuallyDismissed, setManuallyDismissed] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, r, s, u] = await Promise.all([
          api.get("/dashboard"),
          api.get("/referrals/me").catch(() => null),
          api.get("/payments/my-subscription").catch(() => null),
          api.get("/usage/summary").catch(() => null),
        ]);
        setData(d.data);
        if (r) setReferral(r.data);
        if (s) setSubInfo(s.data);
        if (u) setUsage(u.data);
      }
      finally { setLoading(false); }
    })();
  }, []);

  // Preload Razorpay script when expiry banner is likely to show
  useEffect(() => {
    if (subInfo?.active_subscription && (subInfo?.remaining_days ?? 99) <= 7) {
      loadRazorpayScript();
    }
  }, [subInfo]);

  const expiryKey = subInfo?.active_subscription?.expiry_date || "";
  const sessionDismissed = expiryKey ? sessionStorage.getItem(`skillgap_expiry_dismissed_${expiryKey}`) === "1" : false;
  const bannerDismissed = manuallyDismissed || sessionDismissed;

  const dismissBanner = () => {
    if (expiryKey) sessionStorage.setItem(`skillgap_expiry_dismissed_${expiryKey}`, "1");
    setManuallyDismissed(true);
  };

  const renewCurrentPlan = async () => {
    if (!subInfo?.active_subscription?.plan_id) return;
    setRenewing(true);
    await openRazorpayCheckout({
      planId: subInfo.active_subscription.plan_id,
      user,
      onSuccess: (response) => {
        setRenewing(false);
        navigate(`/app/pricing/success?order_id=${response.razorpay_order_id}`);
      },
      onFailure: (response) => {
        setRenewing(false);
        if (response?.razorpay_order_id) {
          navigate(`/app/pricing/success?order_id=${response.razorpay_order_id}&failed=1`);
        }
      },
      onDismiss: () => setRenewing(false),
    });
  };

  const referralUrl = referral?.referral_code
    ? `${window.location.origin}/signup?ref=${referral.referral_code}`
    : "";

  const copyReferral = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link to share");
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await api.get(`/payments/invoice/${orderId}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) toast.error("Pop-up blocked — allow pop-ups to view invoice");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      apiErrorToast(e, "Could not load invoice");
    }
  };

  if (loading) return <div className="p-10"><div className="h-6 w-32 bg-zinc-100 rounded" /></div>;
  if (!data) return <div className="p-10">Failed to load.</div>;

  const sub = data.best_resume_subscores || {};
  const radarData = [
    { axis: "ATS", value: sub.ats_compatibility || 0 },
    { axis: "Format", value: sub.formatting || 0 },
    { axis: "Keywords", value: sub.keyword_optimization || 0 },
    { axis: "Skills", value: sub.skill_relevance || 0 },
    { axis: "Projects", value: sub.project_strength || 0 },
    { axis: "Industry", value: sub.industry_match || 0 },
  ];

  const resumeHistory = (data.history.resume || []).map((r, i) => ({ i: i+1, score: r.overall_score || 0 }));
  const interviewHistory = (data.history.interview || []).map((r, i) => ({ i: i+1, score: r.overall_score || 0 }));
  const skillHistory = (data.history.skill || []).map((r, i) => ({ i: i+1, score: r.match_percent || 0 }));

  const readiness = data.scores.readiness;

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Welcome back</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Hi, {user?.name?.split(" ")[0] || "Candidate"} 👋</h1>
          <p className="text-zinc-600 mt-1 text-sm">Here&apos;s your hiring readiness snapshot.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/resume" data-testid="cta-analyze-resume" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-sm font-semibold hover:opacity-90"><FileSearch size={14}/>Analyze Resume</Link>
          <Link to="/app/interview" data-testid="cta-start-interview" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 text-sm font-semibold hover:border-indigo-500"><MessagesSquare size={14}/>Start Interview</Link>
        </div>
      </div>

      {/* Subscription Expiry Reminder Banner — auto shows when <=7 days remaining */}
      {subInfo?.active_subscription && typeof subInfo.remaining_days === "number" && subInfo.remaining_days <= 7 && !bannerDismissed && (
        <div
          data-testid="expiry-reminder-banner"
          className="mb-6 relative overflow-hidden rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-5 py-4"
        >
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-amber-400 to-rose-400 opacity-20 blur-2xl pointer-events-none" />
          <div className="flex items-start gap-4 flex-wrap relative">
            <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${subInfo.remaining_days <= 1 ? 'bg-rose-500' : 'bg-amber-500'} text-white`}>
              <Clock size={18} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-black text-lg tracking-tight text-zinc-900" data-testid="expiry-banner-title">
                {subInfo.remaining_days <= 0
                  ? "Your subscription expires today"
                  : subInfo.remaining_days === 1
                    ? "Your subscription expires tomorrow"
                    : `Your subscription expires in ${subInfo.remaining_days} days`}
              </div>
              <div className="text-sm text-zinc-700 mt-0.5">
                Renew your <strong>{subInfo.active_subscription.plan_name}</strong> plan now to avoid losing access on <strong>{(subInfo.active_subscription.expiry_date || "").slice(0,10)}</strong>.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={renewCurrentPlan}
                disabled={renewing}
                data-testid="expiry-banner-renew-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {renewing ? <><Loader2 size={14} className="animate-spin"/> Opening…</> : <><Receipt size={14}/> Renew now</>}
              </button>
              <button
                onClick={dismissBanner}
                data-testid="expiry-banner-dismiss-btn"
                aria-label="Dismiss"
                className="h-9 w-9 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-white/60"
              >
                <X size={16}/>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Suspended banner — payment reversed / refund / chargeback */}
      {subInfo?.recent_suspension && (
        <div
          data-testid="suspension-banner"
          className="mb-6 relative overflow-hidden rounded-xl border border-rose-300 bg-gradient-to-r from-rose-50 via-red-50 to-rose-50 px-5 py-4"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="shrink-0 h-10 w-10 rounded-full flex items-center justify-center bg-rose-500 text-white">
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-black text-lg tracking-tight text-rose-900" data-testid="suspension-banner-title">
                Your subscription was suspended
              </div>
              <div className="text-sm text-rose-800 mt-0.5">
                Your <strong>{subInfo.recent_suspension.plan_name}</strong> access was revoked because {subInfo.recent_suspension.suspension_reason || "the payment was reversed"}. Please re-purchase or contact support.
              </div>
            </div>
            <Link
              to="/app/pricing"
              data-testid="suspension-banner-cta"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
            >
              <Receipt size={14}/> Re-purchase
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* READINESS — large card */}
        <div className="card-flat p-6 md:col-span-2 lg:col-span-2 row-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Recruiter Readiness</div>
              <div className="font-display font-black text-6xl mt-2">{readiness}<span className="text-zinc-400 text-2xl">/100</span></div>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background:readinessColor(readiness)+"22", color: readinessColor(readiness) }}>
                {readinessLabel(readiness)}
              </div>
            </div>
            <div className="h-32 w-32 rounded-full flex items-center justify-center" style={{ background: `conic-gradient(${readinessColor(readiness)} ${readiness}%, #F4F4F5 0)` }}>
              <div className="h-[88%] w-[88%] rounded-full bg-white flex items-center justify-center font-mono font-bold text-2xl">
                {readiness}
              </div>
            </div>
          </div>
          <div className="mt-6 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E4E4E7" />
                <PolarAngleAxis dataKey="axis" tick={{ fill:'#52525B', fontSize:11 }} />
                <PolarRadiusAxis tick={{ fill:'#A1A1AA', fontSize:10 }} angle={30} domain={[0,100]} />
                <Radar name="Score" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.18} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <StatCard label="Resume Score" value={data.scores.resume_score} suffix="/100" testid="stat-resume" icon={FileSearch} />
        <StatCard label="ATS Score" value={data.scores.ats_score} suffix="/100" testid="stat-ats" icon={Sparkles} />
        <StatCard label="Interview Score" value={data.scores.interview_score} suffix="/100" testid="stat-interview" icon={MessagesSquare} />
        <StatCard label="Skill Match" value={data.scores.skill_match} suffix="%" testid="stat-skill" icon={Target} />

        {/* Streak + badges */}
        <div className="card-flat p-6 lg:col-span-2">
          <div className="flex items-center gap-3">
            <Flame size={20} className="text-orange-500" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Weekly Streak</div>
              <div className="font-display font-black text-3xl mt-1">{user?.current_streak || 0} <span className="text-base text-zinc-500 font-medium">days</span></div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Best</div>
              <div className="font-mono font-semibold">{user?.best_streak || 0}d</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-7 gap-2">
            {Array.from({length:7}).map((_, i) => (
              <div key={i} className={`h-8 rounded-md ${i < (user?.current_streak||0)%7 ? 'bg-gradient-to-br from-indigo-500 to-sky-500' : 'bg-zinc-100'}`} />
            ))}
          </div>
        </div>

        <div className="card-flat p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award size={18} />
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Badges Earned</div>
            </div>
            <Link to="/app/badges" className="text-xs font-semibold text-indigo-600 inline-flex items-center gap-1">See all <ArrowRight size={12} /></Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(user?.badges || []).length === 0 && <div className="text-sm text-zinc-500">No badges yet — complete an analysis to earn your first one.</div>}
            {(user?.badges || []).map((b) => (
              <div key={b} className="tag tag-info"><Trophy size={12} /> {b.replace(/_/g,' ')}</div>
            ))}
          </div>
        </div>

        {/* Subscription */}
        <div className="card-flat p-6 md:col-span-3 lg:col-span-4" data-testid="subscription-card">
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                <Shield size={14} className="text-indigo-500" /> Subscription
              </div>
              {subInfo?.active_subscription ? (
                <>
                  <div className="font-display font-black text-2xl mt-2">{subInfo.active_subscription.plan_name} plan <span className="rainbow-text">· Active</span></div>
                  <div className="text-sm text-zinc-600 mt-1 flex flex-wrap gap-x-5 gap-y-1">
                    <span><Calendar size={12} className="inline mr-1" /> Activated <strong>{(subInfo.active_subscription.activation_date || "").slice(0,10)}</strong></span>
                    <span>Expires <strong>{(subInfo.active_subscription.expiry_date || "").slice(0,10)}</strong></span>
                    <span><strong className="text-emerald-700">{subInfo.remaining_days}</strong> days remaining</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display font-black text-2xl mt-2">Free plan</div>
                  <div className="text-sm text-zinc-600 mt-1">Pick a plan to unlock unlimited resumes and mock interviews.</div>
                </>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {subInfo?.active_subscription
                ? <>
                    <Link to="/app/pricing" data-testid="upgrade-plan-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-sm font-semibold hover:opacity-90"><ArrowRight size={14}/>Upgrade plan</Link>
                    <Link to="/app/pricing" data-testid="renew-plan-btn" className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-semibold ${subInfo.renew_window_open ? 'border-indigo-500 text-indigo-600 hover:bg-indigo-50' : 'border-zinc-200 text-zinc-400 cursor-not-allowed pointer-events-none'}`}><Receipt size={14}/>{subInfo.renew_window_open ? "Renew now" : `Renew opens ${subInfo.renewal_window_days}d before expiry`}</Link>
                  </>
                : <Link to="/app/pricing" data-testid="choose-plan-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-sm font-semibold hover:opacity-90"><ArrowRight size={14}/>Choose a plan</Link>
              }
            </div>
          </div>

          {/* Usage summary — show Free-tier remaining counters or Unlimited badge */}
          {usage && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="usage-summary">
              {["resume","interview","skillgap"].map((f) => {
                const info = usage.features?.[f] || {};
                const label = f === "resume" ? "Resume Analysis" : f === "interview" ? "Mock Interview" : "Skill-Gap Report";
                const unlimited = info.unlimited;
                const blocked = info.blocked;
                return (
                  <div key={f} data-testid={`usage-${f}`} className={`rounded-lg border px-4 py-3 ${blocked ? 'border-rose-300 bg-rose-50' : unlimited ? 'border-emerald-200 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                    {unlimited ? (
                      <div className="mt-1 font-display font-bold text-lg text-emerald-700">Unlimited <span className="text-xs font-medium text-emerald-600">· Paid plan</span></div>
                    ) : (
                      <div className="mt-1 font-display font-bold text-lg text-zinc-900">
                        <span className="font-mono">{info.used ?? 0}/{info.limit ?? 0}</span>
                        <span className={`ml-2 text-xs font-semibold ${blocked ? 'text-rose-700' : 'text-zinc-500'}`}>{blocked ? 'Limit reached — upgrade' : `${info.remaining ?? 0} left`}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Payment History */}
          {subInfo?.history?.length > 0 && (
            <div className="mt-6 border-t border-zinc-200 pt-4" data-testid="payment-history">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Payment History</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-zinc-500">
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3">Plan</th>
                    <th className="py-1 pr-3">Original</th>
                    <th className="py-1 pr-3">Paid</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Invoice</th>
                  </tr></thead>
                  <tbody>
                    {subInfo.history.slice(0, 6).map((h) => (
                      <tr key={h.order_id} className="border-t border-zinc-100">
                        <td className="py-2 pr-3 font-mono">{(h.created_at || "").slice(0,10)}</td>
                        <td className="py-2 pr-3">{h.plan_name}{h.test_mode && <span className="ml-1 text-[10px] text-zinc-500 font-semibold" title="Historical test-mode payment">LEGACY</span>}</td>
                        <td className="py-2 pr-3">₹{h.original_amount}</td>
                        <td className="py-2 pr-3 font-semibold">₹{h.amount}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${h.payment_status==='paid' ? 'bg-emerald-100 text-emerald-700' : h.payment_status==='failed' ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-600'}`}>{h.payment_status}</span>
                        </td>
                        <td className="py-2 pr-3">
                          {h.payment_status === 'paid'
                            ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/invoice/${h.order_id}?token=${encodeURIComponent(localStorage.getItem("skillgap_token")||"")}`} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); downloadInvoice(h.order_id); }} className="text-indigo-600 font-semibold inline-flex items-center gap-1"><Download size={11}/>Download</a>
                            : <span className="text-zinc-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Refer & Earn */}
        <div className="card-flat p-6 md:col-span-3 lg:col-span-4 relative overflow-hidden" data-testid="refer-earn-card">
          <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-gradient-to-br from-indigo-500 via-sky-500 to-emerald-500 opacity-20 blur-2xl pointer-events-none" />
          <div className="flex items-start justify-between flex-wrap gap-6 relative">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                <Gift size={14} className="text-indigo-500" /> Refer &amp; Earn
              </div>
              <div className="font-display font-black text-2xl mt-2">Invite friends, earn <span className="rainbow-text">₹200</span> per paid signup</div>
              <p className="text-sm text-zinc-600 mt-1 max-w-lg">Share your unique link. When a friend upgrades to any paid plan, we credit ₹200 toward your next renewal.</p>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 ring-rainbow rounded-md px-3 py-2 text-sm font-mono font-semibold tracking-wider" data-testid="referral-code">
                  {referral?.referral_code || "—"}
                </div>
                <button
                  onClick={copyReferral}
                  disabled={!referralUrl}
                  data-testid="copy-referral-link-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy invite link</>}
                </button>
              </div>
              {referralUrl && (
                <div className="text-[11px] text-zinc-500 mt-2 font-mono break-all" data-testid="referral-url">{referralUrl}</div>
              )}
            </div>
            <div className="flex gap-3">
              <div className="text-center px-5 py-3 rounded-lg bg-indigo-50 border border-indigo-100">
                <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-700">Signups</div>
                <div className="font-display font-black text-2xl text-indigo-700 mt-1" data-testid="referral-signups">{referral?.signups ?? 0}</div>
              </div>
              <div className="text-center px-5 py-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">Paid</div>
                <div className="font-display font-black text-2xl text-emerald-700 mt-1" data-testid="referral-paid">{referral?.paid_referrals ?? 0}</div>
              </div>
              <div className="text-center px-5 py-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700">Owed</div>
                <div className="font-display font-black text-2xl text-amber-700 mt-1" data-testid="referral-pending">₹{referral?.pending_reward_inr ?? 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="card-flat p-6 md:col-span-3 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Resume progress</div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={resumeHistory.length ? resumeHistory : [{i:1,score:0}]}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="i" stroke="#A1A1AA" fontSize={10} />
                <YAxis domain={[0,100]} stroke="#A1A1AA" fontSize={10} />
                <Tooltip contentStyle={{ border:'1px solid #E4E4E7', borderRadius:8, fontSize:12 }} />
                <Area type="monotone" dataKey="score" stroke="#6366f1" fill="#6366f1" fillOpacity={0.12} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Interview performance</div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={interviewHistory.length ? interviewHistory : [{i:1,score:0}]}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="i" stroke="#A1A1AA" fontSize={10} />
                <YAxis domain={[0,100]} stroke="#A1A1AA" fontSize={10} />
                <Tooltip contentStyle={{ border:'1px solid #E4E4E7', borderRadius:8, fontSize:12 }} />
                <Line type="monotone" dataKey="score" stroke="#0ea5e9" strokeWidth={2} dot={{ r:3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6 md:col-span-3 lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Skill growth (match %)</div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={skillHistory.length ? skillHistory : [{i:1,score:0}]}>
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="i" stroke="#A1A1AA" fontSize={10} />
                <YAxis domain={[0,100]} stroke="#A1A1AA" fontSize={10} />
                <Tooltip contentStyle={{ border:'1px solid #E4E4E7', borderRadius:8, fontSize:12 }} />
                <Line type="monotone" dataKey="score" stroke="#22C55E" strokeWidth={2} dot={{ r:3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, icon: Icon, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-zinc-500">
        <div className="text-[10px] uppercase tracking-[0.2em]">{label}</div>
        <Icon size={14} />
      </div>
      <div className="font-display font-black text-3xl mt-3 font-mono">{value || 0}<span className="text-zinc-400 text-base">{suffix}</span></div>
      <div className="meter-track mt-3">
        <div className="meter-fill" style={{ width: `${Math.min(100, value||0)}%` }} />
      </div>
    </div>
  );
}
